import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { twMerge } from 'tailwind-merge';

import ErrorBoundary from 'components/ErrorBoundary';

import { landingQueryKeys, topbarQueryKeys } from 'lib/queryKeys';

import { getNavbarAds } from 'services/ads';
import { getLandingsList } from 'services/landing';

import FeaturedLandingLink from '../FeaturedLandings/FeaturedLandingLink';
import NavItem from '../NavItem';

const CombinedNavContent = ({
  className,
  gaEventCategory,
  addContentRef,
  startIndex = 0,
  overflowMap = {},
  showOnlyOverflowed = false,
}) => {
  const { data: navbarAds = [], isLoading: isLoadingAds } = useQuery({
    queryKey: topbarQueryKeys.navbarAds(),
    queryFn: ({ signal }) => getNavbarAds({ signal }),
    select: (data) => data.sort((a, b) => a.position - b.position),
    enabled: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const { data: featuredLandings = [], isLoading: isLoadingLandings } =
    useQuery({
      queryKey: landingQueryKeys.featured(),
      queryFn: ({ signal }) => getLandingsList({ featured: true }, { signal }),
      select: (data) => data.landings,
      enabled: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    });

  const isLoading = isLoadingAds || isLoadingLandings;
  const hasContent = navbarAds?.length > 0 || featuredLandings?.length > 0;

  if (isLoading || !hasContent) {
    return null;
  }

  const shouldShowItem = (index) => {
    if (showOnlyOverflowed) {
      return overflowMap[index] === true;
    }

    return overflowMap[index] !== true;
  };

  return (
    <>
      {navbarAds.map((ad, index) => {
        const itemIndex = startIndex + index;

        if (!shouldShowItem(itemIndex)) {
          return null;
        }

        return (
          <ErrorBoundary key={`ad-${ad.id}-${itemIndex}`}>
            <div
              ref={(el) => addContentRef && addContentRef(itemIndex, el)}
              className={twMerge('flex gap-2', className)}
            >
              <NavItem
                gaEventAction={ad.event || `${ad.title}_tab`}
                gaEventCategory={gaEventCategory}
                imageProps={{
                  src: ad.image,
                  alt: ad.description,
                }}
                sameTab={ad.sameTab}
                text={ad.title}
                unreadStatus={ad.notificationIcon}
                url={ad.url}
              />
            </div>
          </ErrorBoundary>
        );
      })}
      {featuredLandings.map((landing, index) => {
        const itemIndex = startIndex + navbarAds.length + index;

        if (!shouldShowItem(itemIndex)) {
          return null;
        }

        return (
          <ErrorBoundary key={`landing-${landing.landingId}-${itemIndex}`}>
            <div
              ref={(el) => addContentRef && addContentRef(itemIndex, el)}
              className={twMerge('flex gap-2', className)}
            >
              <FeaturedLandingLink
                gaEventCategory={gaEventCategory}
                landingId={landing.landingId}
                landingName={landing.landingName}
                landingSlug={landing.landingSlug}
                landingType={landing.landingType}
              />
            </div>
          </ErrorBoundary>
        );
      })}
    </>
  );
};

CombinedNavContent.propTypes = {
  className: PropTypes.string,
  gaEventCategory: PropTypes.string.isRequired,
  addContentRef: PropTypes.func,
  startIndex: PropTypes.number,
  overflowMap: PropTypes.object,
  showOnlyOverflowed: PropTypes.bool,
};

CombinedNavContent.displayName = 'CombinedNavContent';

export default CombinedNavContent;
